import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Download, FileUp, Loader2 } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import type { StockImportResult } from '@/types'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { fmtQty } from '@/lib/quantity'
import { toastHelpers } from '@/lib/toast-helpers'
import { IM } from './itemMasterStrings'
import { canCommitImport, dataUrlToBase64, describeBarcodes, describeImportExpiry, previewRows } from './importPreview'

const MAX_BYTES = 5 * 1024 * 1024

/**
 * Bulk import of stock items (every tenant; admin + inventory_manager).
 * Download template → choose the filled file → the server checks every row
 * (dry run) → a preview grid with problem rows in red → one button imports
 * all of it in a single transaction. Nothing is saved while any row is red.
 */
export function ImportItemsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null)
  const [result, setResult] = useState<StockImportResult | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  function reset() {
    setFile(null)
    setResult(null)
    setFileError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const check = useMutation({
    mutationFn: (f: { name: string; base64: string }) => apiClient.importStockItems(f.name, f.base64, true),
    onSuccess: (res) => setResult(res.data ?? null),
    onError: (err: Error) => setFileError(err instanceof ApiClientError && err.message ? err.message.split(' — ')[0] : err.message),
  })

  const commit = useMutation({
    mutationFn: (f: { name: string; base64: string }) => apiClient.importStockItems(f.name, f.base64, false),
    onSuccess: (res) => {
      for (const key of ['stockItems', 'stockAlerts', 'stockCategories', 'stockSummary', 'advancedStockReport', 'admin-stock-item-options', 'admin-stock-categories']) {
        qc.invalidateQueries({ queryKey: [key] })
      }
      toastHelpers.success('Inventory', IM.import.done(res.data?.created ?? 0))
      reset()
      onClose()
    },
    onError: (err: Error) => {
      // Someone added a clashing item since the preview: show the fresh check.
      if (err instanceof ApiClientError && err.code === 'import_has_errors' && err.payload) {
        setResult(err.payload as StockImportResult)
        return
      }
      toastHelpers.error('Import failed', err.message)
    },
  })

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0]
    setResult(null)
    setFileError(null)
    if (!picked) return
    if (picked.size > MAX_BYTES) {
      setFileError(IM.import.tooBig)
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const f = { name: picked.name, base64: dataUrlToBase64(String(reader.result ?? '')) }
      setFile(f)
      check.mutate(f)
    }
    reader.onerror = () => setFileError('The file could not be read')
    reader.readAsDataURL(picked)
  }

  const { shown, hidden } = previewRows(result?.rows ?? [])

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose() } }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{IM.import.title}</DialogTitle>
          <DialogDescription>{IM.import.step1}</DialogDescription>
        </DialogHeader>

        <Button
          type="button" variant="outline" className="w-full sm:w-auto"
          onClick={() => apiClient.downloadStockImportTemplate().catch((e: Error) => toastHelpers.error('Download failed', e.message))}
        >
          <Download className="mr-2 h-4 w-4" /> {IM.import.download}
        </Button>

        <p className="text-sm text-muted-foreground">{IM.import.step2}</p>
        <input ref={fileRef} type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="hidden" onChange={onPick} />
        <Button type="button" className="w-full sm:w-auto" onClick={() => fileRef.current?.click()} disabled={check.isPending || commit.isPending}>
          <FileUp className="mr-2 h-4 w-4" /> {file ? file.name : IM.import.choose}
        </Button>

        {check.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {IM.import.checking}</p>
        )}
        {fileError && (
          <p className="flex items-start gap-2 rounded-md bg-danger-surface/50 px-3 py-2 text-sm text-danger-fg">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {fileError}
          </p>
        )}

        {result && (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${result.error_rows > 0 ? 'bg-danger-surface/50 text-danger-fg' : 'bg-success-surface/50 text-success-fg'}`}>
              {result.error_rows > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              {IM.import.summary(result.valid_rows, result.error_rows)}
            </div>
            {result.error_rows > 0 && <p className="text-sm">{IM.import.fixFirst}</p>}
            {result.new_categories.length > 0 && <p className="text-sm text-muted-foreground">{IM.import.newCategories(result.new_categories)}</p>}
            {result.warnings.map((w) => <p key={w} className="text-sm text-warning-fg">{w}</p>)}

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2">{IM.import.colRow}</th>
                    <th className="px-2 py-2">{IM.import.colName}</th>
                    <th className="px-2 py-2">{IM.import.colUnit}</th>
                    <th className="px-2 py-2 text-right">{IM.import.colOpening}</th>
                    <th className="px-2 py-2 text-right">{IM.import.colCost}</th>
                    <th className="px-2 py-2">{IM.import.colExpiry}</th>
                    <th className="px-2 py-2">{IM.import.colBarcodes}</th>
                    <th className="px-2 py-2">{IM.import.colProblem}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {shown.map((r) => {
                    const bad = r.errors.length > 0
                    return (
                      <tr key={r.row} className={bad ? 'bg-danger-surface/40' : undefined}>
                        <td className="px-2 py-2 tabular-nums text-muted-foreground">{r.row}</td>
                        <td className="px-2 py-2 font-medium">
                          {r.name || '—'}
                          {r.category && <span className="block text-xs font-normal text-muted-foreground">{r.category}{r.category_is_new ? ' (new)' : ''}</span>}
                        </td>
                        <td className="px-2 py-2">
                          {r.unit}
                          {r.purchase_unit_label && <span className="block text-xs text-muted-foreground">{r.purchase_unit_label} = {fmtQty(r.purchase_unit_factor ?? 0)}</span>}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtQty(r.opening_qty)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{r.cost != null ? fmtQty(r.cost) : '—'}</td>
                        <td className="px-2 py-2 text-xs">{describeImportExpiry(r) || '—'}</td>
                        <td className="px-2 py-2 text-xs">{describeBarcodes(r.barcodes) || '—'}</td>
                        <td className={`px-2 py-2 text-xs ${bad ? 'text-danger-fg font-medium' : 'text-success-fg'}`}>
                          {bad ? r.errors.join('; ') : IM.import.ok}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {hidden > 0 && <p className="px-3 py-2 text-xs text-muted-foreground">+ {hidden} more rows, all OK</p>}
            </div>

            <Button
              type="button" className="w-full"
              disabled={!canCommitImport(result) || !file || commit.isPending}
              onClick={() => file && commit.mutate(file)}
            >
              {commit.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {IM.import.importing}</> : IM.import.confirm(result.valid_rows)}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
